import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { workshopApi, uploadPaymentProof } from "../../services/workshopApi";
import { useUserContext } from "../../context/UserContext";
import PaymentInstructionsDisplay from "./PaymentInstructions";
import LoadingSpinner from "../ui/LoadingSpinner";
import "./EditRequest.css";

const EditRequest = ({ workshopId: propWorkshopId }) => {
  const params = useParams();
  // Support both prop-based (from PersistentWorkshopsLayout) and route-based workshopId
  const workshopId = propWorkshopId || params.workshopId;
  const navigate = useNavigate();
  const { user: currentUser } = useUserContext();

  const [workshop, setWorkshop] = useState(null);
  const [currentApplication, setCurrentApplication] = useState(null);
  const [signedImageUrl, setSignedImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    paymentMethod: "bank_transfer",
    transactionId: "",
    notes: "", // Payment notes
    applicationNotes: "", // Application/motivation notes
    selectedFile: null,
  });

  useEffect(() => {
    fetchData();
  }, [workshopId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch workshop details and current application in parallel
      const [workshopResponse, applicationResponse] = await Promise.all([
        workshopApi.getWorkshopById(workshopId),
        workshopApi.getMyCurrentApplication(workshopId),
      ]);

      if (workshopResponse.success) {
        const workshopData = workshopResponse.data.workshop;
        setWorkshop(workshopData);

        // 🔍 DEBUG: Check what the backend is sending
}

      if (applicationResponse.success) {
        const app = applicationResponse.data.application;
        setCurrentApplication(app);
// Pre-fill form with existing data
        setFormData({
          paymentMethod: app.paymentProof?.paymentMethod || "bank_transfer",
          transactionId: app.paymentProof?.transactionId || "",
          notes: app.paymentProof?.additionalNotes || "",
          applicationNotes: app.notes || "", // Application notes from participant.notes
          selectedFile: null, // Don't pre-select file, user needs to choose new one if they want to change
        });
      }

      // ✅ NEW: Get signed URL for payment proof image if user has uploaded one
      if (
        workshopResponse.success &&
        workshopResponse.data.workshop.userPaymentProof?.imageKey
      ) {
        try {
          const signedUrlResponse = await workshopApi.getPaymentProofSignedUrl(
            workshopId
          );
          if (signedUrlResponse.success) {
            setSignedImageUrl(signedUrlResponse.data.imageUrl);
}
        } catch (signedUrlError) { /* ignored */ }
      }
    } catch (err) {
setError("Failed to load application data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        setError("Please select a valid image file (JPEG, PNG, GIF)");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        setError("File size must be less than 5MB");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        selectedFile: file,
      }));
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.transactionId.trim()) {
      setError("Transaction ID is required");
      return;
    }

    if (!formData.applicationNotes.trim()) {
      setError("Application notes are required");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Prepare update data - both application and payment fields
      const updateData = {
        notes: formData.applicationNotes.trim(), // Application notes
        paymentProof: {
          transactionId: formData.transactionId.trim(),
          paymentMethod: formData.paymentMethod,
          additionalNotes: formData.notes.trim(), // Payment notes
        },
      };

      // If user selected a new file, upload it first
      if (formData.selectedFile) {
        const uploadResponse = await uploadPaymentProof(
          workshopId,
          formData.selectedFile,
          {
            transactionId: formData.transactionId.trim(),
            paymentMethod: formData.paymentMethod,
            notes: formData.notes.trim(),
          }
        );

        if (!uploadResponse.success) {
          throw new Error(
            uploadResponse.message || "Failed to upload payment proof"
          );
        }

        // Use the uploaded file data
        updateData.paymentProof = {
          ...updateData.paymentProof,
          imageKey: uploadResponse.data.imageKey,
          imageUrl: uploadResponse.data.imageUrl,
        };
      }

      // Update the application
      const response = await workshopApi.editMyApplication(
        workshopId,
        updateData
      );

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/workshops/${workshopId}`);
        }, 2000);
      } else {
        throw new Error(response.message || "Failed to update application");
      }
    } catch (err) {
setError(
        err.message || "Failed to update application. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-request-loading">
        <LoadingSpinner />
        <p>Loading your application...</p>
      </div>
    );
  }

  if (error && !workshop) {
    return (
      <div className="edit-request-error">
        <h3>Error</h3>
        <p>{error}</p>
        <button
          onClick={() => navigate(`/workshops/${workshopId}`)}
          className="back-btn"
        >
          Back to Workshop
        </button>
      </div>
    );
  }

  if (!currentApplication) {
    return (
      <div className="edit-request-error">
        <h3>No Application Found</h3>
        <p>You haven't applied to this workshop yet.</p>
        <button
          onClick={() => navigate(`/workshops/${workshopId}`)}
          className="back-btn"
        >
          Back to Workshop
        </button>
      </div>
    );
  }

  return (
    <div className="edit-request-container">
      <div className="edit-request-header">
        <button
          onClick={() => navigate(`/workshops/${workshopId}`)}
          className="back-button"
        >
          ← Back to Workshop
        </button>
        <h2>Edit Your Application</h2>
        <p>Update your payment details and application information</p>
      </div>

      <div className="edit-request-content">
        {/* Workshop Info */}
        <div className="workshop-info-card">
          <h3>{workshop.title}</h3>
          <div className="workshop-meta">
            <span className="workshop-price">
              💰 {workshop.pricing.amount} {workshop.pricing.currency}
            </span>
            <span className="workshop-date">
              📅 {new Date(workshop.duration.startDate).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Current Application Status */}
        <div className="current-application-card">
          <h4>📋 Current Application Status</h4>
          <div className="application-status">
            <span className={`status-badge ${currentApplication.status}`}>
              {currentApplication.status === "payment_pending"
                ? "💳 Payment Pending"
                : currentApplication.status}
            </span>
            <span className="applied-date">
              Applied:{" "}
              {new Date(currentApplication.appliedAt).toLocaleDateString()}
            </span>
          </div>

          {/* Show current payment proof if exists */}
          {(workshop?.userPaymentProof || currentApplication?.paymentProof) && (
            <div className="current-payment-proof">
              <h5>Current Payment Proof:</h5>
              <div className="payment-proof-preview">
                {/* ✅ FIXED: Use workshop.userPaymentProof.imageUrl as per backend engineer */}
                {(() => {
// ✅ PRIORITY 1: Use signed URL if available
                  if (signedImageUrl) {
                    return (
                      <img
                        src={signedImageUrl}
                        alt="Current payment proof"
                        className="payment-proof-image"
                        onClick={() => window.open(signedImageUrl, "_blank")}
                        title="Click to view full size"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextElementSibling.style.display = "block";
                        }}
                        onLoad={() => { /* loaded */ }}
                      />
                    );
                  }
                  // ✅ FALLBACK: Try direct URL (will likely fail due to CORS)
                  const directImageUrl =
                    workshop?.userPaymentProof?.imageUrl ||
                    currentApplication?.paymentProof?.imageUrl;
                  if (directImageUrl) {
                    return (
                      <div className="image-container">
                        <img
                          src={directImageUrl}
                          alt="Current payment proof"
                          className="payment-proof-image"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextElementSibling.style.display = "block";
                          }}
                          onLoad={() => { /* loaded */ }}
                        />
                      </div>
                    );
                  }
                  // ✅ NO IMAGE: Show placeholder
                  return (
                    <div className="no-image-placeholder">
                      <p>📷 No payment proof image found</p>
                      <small>Image URL not available</small>
                    </div>
                  );
                })()}
                <div className="payment-proof-details">
                  <p>
                    <strong>Transaction ID:</strong>{" "}
                    {workshop?.userPaymentProof?.transactionId ||
                      currentApplication?.paymentProof?.transactionId}
                  </p>
                  <p>
                    <strong>Uploaded:</strong>{" "}
                    {new Date(
                      workshop?.userPaymentProof?.uploadedAt ||
                        currentApplication?.paymentProof?.uploadedAt
                    ).toLocaleDateString()}
                  </p>
                  <p>
                    <strong>Status:</strong>
                    <span
                      className={`payment-status ${
                        workshop?.userPaymentProof?.status ||
                        currentApplication?.paymentProof?.status
                      }`}
                    >
                      {workshop?.userPaymentProof?.status ||
                        currentApplication?.paymentProof?.status}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payment Instructions */}
        <div className="payment-instructions-card">
          <PaymentInstructionsDisplay workshop={workshop} showTitle={true} />
        </div>

        {/* Edit Form */}
        <form onSubmit={handleSubmit} className="edit-request-form">
          <h4>✏️ Update Your Application</h4>

          {/* Transaction ID */}
          <div className="form-field">
            <label>Transaction ID *</label>
            <input
              type="text"
              value={formData.transactionId}
              onChange={(e) =>
                handleInputChange("transactionId", e.target.value)
              }
              placeholder="Enter your transaction ID"
              required
            />
          </div>

          {/* Payment Method */}
          <div className="form-field">
            <label>Payment Method</label>
            <select
              value={formData.paymentMethod}
              onChange={(e) =>
                handleInputChange("paymentMethod", e.target.value)
              }
            >
              <option value="bank_transfer">Bank Transfer</option>
              <option value="easypaisa">EasyPaisa</option>
              <option value="jazzcash">JazzCash</option>
              <option value="paypal">PayPal</option>
              <option value="wise">Wise</option>
            </select>
          </div>

          {/* New Payment Proof (Optional) */}
          <div className="form-field file-upload-field">
            <label>📎 Update Payment Proof (Optional)</label>
            <div className="file-upload-container">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="file-input"
                id="payment-proof-file"
              />
              <label htmlFor="payment-proof-file" className="file-upload-label">
                {formData.selectedFile ? (
                  <>
                    ✅ {formData.selectedFile.name}
                    <small>
                      ({(formData.selectedFile.size / 1024 / 1024).toFixed(2)}{" "}
                      MB)
                    </small>
                  </>
                ) : (
                  <>
                    📷 Choose New Payment Proof
                    <small>Click to select image file</small>
                  </>
                )}
              </label>
            </div>
            <div className="file-help">
              💡 <strong>Optional:</strong> Leave empty to keep your current
              payment proof. Upload a new image only if you want to replace it.
            </div>
          </div>

          {/* Application Notes */}
          <div className="form-field">
            <label>Application Notes *</label>
            <textarea
              value={formData.applicationNotes}
              onChange={(e) =>
                handleInputChange("applicationNotes", e.target.value)
              }
              placeholder="Why do you want to join this workshop? What's your motivation and relevant experience?"
              rows={4}
              required
            />
          </div>

          {/* Payment Notes */}
          <div className="form-field">
            <label>Payment Notes (Optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder="Add any payment-specific information (e.g., payment method details, reference notes)..."
              rows={3}
            />
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          {success && (
            <div className="success-message">
              <p>✅ Application updated successfully! Redirecting...</p>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="workshop-edit-form-actions">
            <button
              type="button"
              onClick={() => navigate(`/workshops/${workshopId}`)}
              className="cancel-btn"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={saving || !formData.transactionId.trim()}
            >
              {saving ? (
                <>
                  <LoadingSpinner size="small" />
                  Updating...
                </>
              ) : (
                "Update Application"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRequest;
